import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, RotateCcw, Beaker, Thermometer, Droplets, TestTube2 } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ALERT_LIMITS_HELP, ALERT_LIMITS_TITLE } from './alertLimitsText';

interface Thresholds {
  ph_min: number;
  ph_max: number;
  do_min: number;
  do_max: number;
  temp_min: number;
  temp_max: number;
  ammonia_max: number;
  nitrite_max: number;
  salinity_min: number;
  salinity_max: number;
}

interface FarmResponse {
  data: {
    id: string;
    name: string;
    settings: {
      alert_thresholds: Thresholds;
    };
  };
}

const DEFAULT_THRESHOLDS: Thresholds = {
  ph_min: 7.0,
  ph_max: 9.0,
  do_min: 4.0,
  do_max: 15.0,
  temp_min: 23.0,
  temp_max: 33.0,
  ammonia_max: 1.0,
  nitrite_max: 1.0,
  salinity_min: 10.0,
  salinity_max: 35.0,
};

interface ThresholdConfigProps {
  farmId: string;
}

export function ThresholdConfig({ farmId }: ThresholdConfigProps) {
  const queryClient = useQueryClient();
  const [draftThresholds, setDraftThresholds] = useState<Thresholds | null>(null);

  const { data: farm, isLoading } = useQuery({
    queryKey: ['farm', farmId],
    queryFn: () => api.get<FarmResponse>(`/api/v1/farms/${farmId}`),
  });

  const farmThresholds = farm?.data?.settings?.alert_thresholds ?? DEFAULT_THRESHOLDS;
  const thresholds = draftThresholds ?? farmThresholds;

  const hasChanges = useMemo(
    () => !!draftThresholds && !thresholdsEqual(draftThresholds, farmThresholds),
    [draftThresholds, farmThresholds]
  );

  const saveMutation = useMutation({
    mutationFn: (newThresholds: Thresholds) =>
      api.put(`/api/v1/farms/${farmId}/settings`, {
        alert_thresholds: newThresholds,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['farm', farmId] });
      setDraftThresholds(null);
    },
  });

  const handleChange = (key: keyof Thresholds, value: string) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return;

    setDraftThresholds((prev) => {
      const base = prev ?? farmThresholds;
      return { ...base, [key]: numValue };
    });
  };

  const handleReset = () => {
    setDraftThresholds(DEFAULT_THRESHOLDS);
  };

  if (isLoading && !farm) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <LoadingSpinner size={32} />
        <p className="text-sm text-gray-500">Loading thresholds...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{ALERT_LIMITS_TITLE}</h3>
          <p className="text-sm text-gray-500 mt-0.5">{ALERT_LIMITS_HELP}</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            onClick={handleReset}
            leftIcon={<RotateCcw className="w-4 h-4" />}
          >
            Reset
          </Button>
          <Button
            onClick={() => thresholds && saveMutation.mutate(thresholds)}
            disabled={!hasChanges}
            loading={saveMutation.isPending}
            leftIcon={<Save className="w-4 h-4" />}
          >
            Save Changes
          </Button>
        </div>
      </div>

      {/* Unsaved Changes Indicator */}
      {hasChanges && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-700">
          <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
          You have unsaved changes
        </div>
      )}

      {/* pH Section */}
      <ThresholdSection
        title="pH Levels"
        description="Acceptable pH range for water quality"
        icon={Beaker}
        iconColor="text-purple-600"
        iconBg="bg-purple-100"
      >
        <div className="grid grid-cols-2 gap-4">
          <ThresholdInput
            label="Minimum pH"
            value={thresholds.ph_min}
            onChange={(v) => handleChange('ph_min', v)}
            step="0.1"
            min="0"
            max="14"
          />
          <ThresholdInput
            label="Maximum pH"
            value={thresholds.ph_max}
            onChange={(v) => handleChange('ph_max', v)}
            step="0.1"
            min="0"
            max="14"
          />
        </div>
      </ThresholdSection>

      {/* Dissolved Oxygen Section */}
      <ThresholdSection
        title="Dissolved Oxygen (DO)"
        description="Minimum and maximum DO levels in mg/L"
        icon={Droplets}
        iconColor="text-cyan-600"
        iconBg="bg-cyan-100"
      >
        <div className="grid grid-cols-2 gap-4">
          <ThresholdInput
            label="Minimum DO (mg/L)"
            value={thresholds.do_min}
            onChange={(v) => handleChange('do_min', v)}
            step="0.1"
            min="0"
          />
          <ThresholdInput
            label="Maximum DO (mg/L)"
            value={thresholds.do_max}
            onChange={(v) => handleChange('do_max', v)}
            step="0.1"
            min="0"
          />
        </div>
      </ThresholdSection>

      {/* Temperature Section */}
      <ThresholdSection
        title="Temperature"
        description="Acceptable water temperature range in Celsius"
        icon={Thermometer}
        iconColor="text-orange-600"
        iconBg="bg-orange-100"
      >
        <div className="grid grid-cols-2 gap-4">
          <ThresholdInput
            label="Minimum Temp (°C)"
            value={thresholds.temp_min}
            onChange={(v) => handleChange('temp_min', v)}
            step="0.1"
          />
          <ThresholdInput
            label="Maximum Temp (°C)"
            value={thresholds.temp_max}
            onChange={(v) => handleChange('temp_max', v)}
            step="0.1"
          />
        </div>
      </ThresholdSection>

      {/* Chemical Parameters Section */}
      <ThresholdSection
        title="Chemical Parameters"
        description="Maximum levels for ammonia and nitrite"
        icon={TestTube2}
        iconColor="text-rose-600"
        iconBg="bg-rose-100"
      >
        <div className="grid grid-cols-2 gap-4">
          <ThresholdInput
            label="Max Ammonia (ppm)"
            value={thresholds.ammonia_max}
            onChange={(v) => handleChange('ammonia_max', v)}
            step="0.1"
            min="0"
          />
          <ThresholdInput
            label="Max Nitrite (ppm)"
            value={thresholds.nitrite_max}
            onChange={(v) => handleChange('nitrite_max', v)}
            step="0.01"
            min="0"
          />
        </div>
      </ThresholdSection>

      {/* Salinity Section */}
      <ThresholdSection
        title="Salinity"
        description="Acceptable salinity range in ppt"
        icon={Droplets}
        iconColor="text-teal-600"
        iconBg="bg-teal-100"
      >
        <div className="grid grid-cols-2 gap-4">
          <ThresholdInput
            label="Minimum Salinity (ppt)"
            value={thresholds.salinity_min}
            onChange={(v) => handleChange('salinity_min', v)}
            step="0.1"
            min="0"
          />
          <ThresholdInput
            label="Maximum Salinity (ppt)"
            value={thresholds.salinity_max}
            onChange={(v) => handleChange('salinity_max', v)}
            step="0.1"
            min="0"
          />
        </div>
      </ThresholdSection>
    </div>
  );
}

function thresholdsEqual(left: Thresholds, right: Thresholds): boolean {
  return (
    left.ph_min === right.ph_min &&
    left.ph_max === right.ph_max &&
    left.do_min === right.do_min &&
    left.do_max === right.do_max &&
    left.temp_min === right.temp_min &&
    left.temp_max === right.temp_max &&
    left.ammonia_max === right.ammonia_max &&
    left.nitrite_max === right.nitrite_max &&
    left.salinity_min === right.salinity_min &&
    left.salinity_max === right.salinity_max
  );
}

interface ThresholdSectionProps {
  title: string;
  description: string;
  icon: typeof Beaker;
  iconColor: string;
  iconBg: string;
  children: React.ReactNode;
}

function ThresholdSection({
  title,
  description,
  icon: Icon,
  iconColor,
  iconBg,
  children,
}: ThresholdSectionProps) {
  return (
    <Card>
      <div className="p-5">
        <div className="flex items-start gap-4 mb-4">
          <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center shrink-0', iconBg)}>
            <Icon className={cn('w-5 h-5', iconColor)} />
          </div>
          <div>
            <h4 className="font-medium text-gray-900">{title}</h4>
            <p className="text-sm text-gray-500 mt-0.5">{description}</p>
          </div>
        </div>
        {children}
      </div>
    </Card>
  );
}

interface ThresholdInputProps {
  label: string;
  value: number;
  onChange: (value: string) => void;
  step?: string;
  min?: string;
  max?: string;
}

function ThresholdInput({ label, value, onChange, step = '0.1', min, max }: ThresholdInputProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        step={step}
        min={min}
        max={max}
        className={cn(
          'block w-full rounded-lg px-3.5 py-2.5',
          'text-gray-900 text-sm font-mono',
          'bg-white border border-gray-300',
          'focus:border-primary-500 focus:ring-primary-500/20',
          'focus:outline-none focus:ring-4',
          'transition-all duration-150'
        )}
      />
    </div>
  );
}
